/**
 * [Phase 2c.6-A.1.11 RL-9 / RL-10 / RL-11 / RL-18 / RL-19 / RL-20]
 * DraftEndRecapModal - Adam's LLM-driven recap on DRAFT_COMPLETE.
 *
 * Mounted by SnakeDraftRoom + DraftRoom when draftState.state === "DRAFT_COMPLETE".
 * Both Live and Mock formats use this same modal. After dismissal:
 *   - Mock: MockDraftCompleteModal mounts next (z-[60])
 *   - Live: 5s timer → nav to /league/:id/home
 *
 * Architecture:
 *   - Mount → POST /api/season/{leagueId}/draft-end-recap (lazy LLM, idempotent)
 *   - Shimmer "Adam's clearing his throat..." while in-flight (RL-18)
 *   - Resolves to voice_text → render + auto-play TTS via playLine()
 *   - Dismissal triggers stopAll() (RL-19 ensures Promise resolves clean)
 *   - Skippable via: close X | ESC | click-outside | 1s after TTS ends
 *
 * Failure path (RL-18): LLM 500/empty → modal unmounts gracefully, console.warn,
 * NO retries (Adam stays silent rather than queue-flood the LLM).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Download, Users, CalendarDays, LayoutDashboard } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";
import { playLine, stopAll } from "../../audio/auctioneerAudio";
import AdamAvatarBadge from "../AdamAvatarBadge";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DraftEndRecapModal({ leagueId, onDismiss, surface = "draft-room" }) {
  const [voiceText, setVoiceText] = useState(null);
  // [Item 4] Post-draft exits. `results` tells us who may export and whether
  // a season even exists for this league.
  const [results, setResults] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const cancelRef = useRef(false);
  const ttsEndedAtRef = useRef(null);

  // [HF4.5b addendum] Auto-play surface gate.
  //   - "standalone-board" → auto-play on text arrival (matches second-screen
  //     mental model; the board IS the room's audio surface).
  //   - "draft-room"      → MUTE BY DEFAULT. The user clicks the speaker
  //     button (top-right) to opt into audio. Eliminates cross-surface echo
  //     when both surfaces are open without needing a cross-tab lock.
  //
  // Replaced HF4.5b's localStorage lock with this simpler surface gate per
  // founder direction: "it mutes on default on dashboard, however, until
  // user clicks the audio replay."
  const nav = useNavigate();

  const playRecap = useCallback(async (text) => {
    if (!text || cancelRef.current) return;
    setIsPlaying(true);
    try {
      await playLine(text, "RECAP");
      ttsEndedAtRef.current = Date.now();
    } finally {
      setIsPlaying(false);
    }
  }, []);

  const toggleAudio = useCallback(() => {
    if (isPlaying) {
      stopAll();
      setIsPlaying(false);
      return;
    }
    if (voiceText) {
      playRecap(voiceText);
    }
  }, [isPlaying, voiceText, playRecap]);

  // [HF4.5b addendum] Cross-tab audio lock removed. The surface-gated
  // auto-play above (standalone-board only) plus the draft-room speaker
  // button replaces it cleanly: draft-room never auto-plays so no echo is
  // possible regardless of how many tabs are open.

  // Lazy POST on mount. Idempotent server-side - safe across reloads.
  // [HF4-2] Bounded retry on HTTP 400 ("Draft not complete"). Race:
  // `_broadcast_state` (engines/snake_draft.py:193) does `broadcast` BEFORE
  // `persist`, so clients receive DRAFT_COMPLETE while DB still says
  // league.status="DRAFTING". The /draft-end-recap endpoint gates on
  // league.status, so the first attempt can hit a transient 400. The race
  // self-resolves within ~1s as persist commits. Three retries × 500ms.
  // Only terminal failures (LLM 500, empty voice_text, or 400 still after
  // retries) take the silent-dismiss path; transient 400 retries silently.
  useEffect(() => {
    let cancelled = false;
    const RETRY_ON_400 = 3;
    const RETRY_DELAY_MS = 500;
    const isStatus400 = (e) => e?.response?.status === 400;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      let lastErr = null;
      for (let attempt = 0; attempt <= RETRY_ON_400; attempt += 1) {
        try {
          const res = await axios.post(
            `${API}/api/season/${leagueId}/draft-end-recap`,
            {},
            { headers: authHeaders() },
          );
          if (cancelled || cancelRef.current) return;
          const text = res?.data?.voice_text;
          if (!text) throw new Error("empty voice_text");
          setVoiceText(text);
          // [HF4.5b addendum] Surface-gated auto-play. Standalone board
          // plays automatically; draft-room waits for user click.
          if (surface === "standalone-board") {
            playRecap(text);
          }
          return;
        } catch (e) {
          lastErr = e;
          // Transient: backend race between broadcast & persist of
          // league.status. Retry quietly.
          if (isStatus400(e) && attempt < RETRY_ON_400) {
            await sleep(RETRY_DELAY_MS);
            if (cancelled || cancelRef.current) return;
            continue;
          }
          break;
        }
      }
      // Terminal failure path (RL-18): no further retries, console-warn,
      // gracefully unmount so downstream UX (Mock modal / bridge nav) can fire.
      // eslint-disable-next-line no-console
      console.warn(
        "[DraftEndRecapModal] terminal failure, dismissing:",
        lastErr?.message || lastErr,
      );
      if (cancelled || cancelRef.current) return;
      setError(true);
      // Brief delay so the user doesn't see a flicker.
      setTimeout(() => onDismiss?.(), 200);
    })();
    return () => { cancelled = true; };
  }, [leagueId, onDismiss, surface, playRecap]);

  const handleDismiss = useCallback(() => {
    if (cancelRef.current) return;
    cancelRef.current = true;
    stopAll();
    onDismiss?.();
  }, [onDismiss]);

  // [Item 4] One payload drives the exits AND the CSV.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/draft/${leagueId}/results`, { headers: authHeaders() })
      .then((r) => { if (!cancelled) setResults(r.data); })
      .catch(() => { /* exits degrade to Dashboard + View Teams */ });
    return () => { cancelled = true; };
  }, [leagueId]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/draft/${leagueId}/results.csv`, {
        headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(results?.league?.name || "league").replace(/[^\w-]+/g, "_")}_draft_results.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [leagueId, results?.league?.name]);

  const goTo = useCallback((path) => {
    stopAll();
    nav(path);
  }, [nav]);

  // ESC key to dismiss.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleDismiss]);

  if (error) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md"
        onClick={handleDismiss}
        data-testid="draft-end-recap-modal-backdrop"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 8 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 8 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative w-full max-w-2xl mx-4 p-8 sm:p-10 rounded-2xl bg-[#0F0F12]"
          style={{
            border: "2px solid rgba(255,107,0,0.85)",
            boxShadow:
              "0 0 0 1px rgba(255,107,0,0.3), 0 0 32px rgba(255,107,0,0.45), 0 24px 56px rgba(0,0,0,0.6)",
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="draft-end-recap-modal"
        >
          {/* [HF4.5b addendum] Speaker / replay button - top-right, next
              to the close X. Always visible once voice_text loaded.
              Behavior:
                - draft-room surface: starts muted; click to play (founder spec)
                - standalone-board: auto-played already; click to replay/stop */}
          {voiceText && (
            <button
              onClick={toggleAudio}
              className="absolute top-4 right-14 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="draft-end-recap-audio-toggle"
              aria-label={isPlaying ? "Stop recap audio" : "Play recap audio"}
              title={isPlaying ? "Stop" : "Play recap"}
            >
              {isPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </button>
          )}

          {/* Close X */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="draft-end-recap-close"
            aria-label="Dismiss recap"
          >
            <X size={20} />
          </button>

          {/* [Phase 2c.6-A.2.1 D-6 / RL-34] Unified Adam badge component. */}
          <div className="mb-6">
            <AdamAvatarBadge label="ADAM · END OF DRAFT" size="md" />
          </div>

          {/* Body: shimmer or text */}
          {voiceText ? (
            <p
              className="text-white text-base sm:text-lg leading-relaxed font-sans whitespace-pre-line"
              data-testid="draft-end-recap-text"
            >
              {voiceText}
            </p>
          ) : (
            <div className="space-y-3" data-testid="draft-end-recap-shimmer">
              <div className="h-4 bg-white/10 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-white/10 rounded animate-pulse w-full" />
              <div className="h-4 bg-white/10 rounded animate-pulse w-5/6" />
              <div className="h-4 bg-white/10 rounded animate-pulse w-2/3" />
              <p className="pt-4 text-sm text-white/40 italic">
                Adam's clearing his throat...
              </p>
            </div>
          )}

          {/* [Item 4] Post-draft exits. Export is commissioner-only; Go To
              Season only exists if this league actually has one. */}
          {voiceText && (
            <div className="mt-8 flex flex-wrap gap-2 justify-end">
              {results?.is_commissioner && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#FF6B00]/60 text-[#FF6B00] hover:bg-[#FF6B00]/10 disabled:opacity-60 font-display font-bold uppercase tracking-wider text-sm transition-colors"
                  data-testid="draft-end-export-league-btn"
                >
                  <Download size={15} />
                  {exporting ? "Exporting" : "Export League"}
                </button>
              )}
              <button
                onClick={() => goTo(`/league/${leagueId}/teams`)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/20 text-white/85 hover:bg-white/10 font-display font-bold uppercase tracking-wider text-sm transition-colors"
                data-testid="draft-end-view-teams-btn"
              >
                <Users size={15} /> View Teams
              </button>
              {results?.has_season && (
                <button
                  onClick={() => goTo(`/league/${leagueId}/home`)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase tracking-wider text-sm transition-colors"
                  data-testid="draft-end-go-to-season-btn"
                >
                  <CalendarDays size={15} /> Go To Season
                </button>
              )}
              <button
                onClick={() => goTo("/dashboard")}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/20 text-white/85 hover:bg-white/10 font-display font-bold uppercase tracking-wider text-sm transition-colors"
                data-testid="draft-end-dashboard-btn"
              >
                <LayoutDashboard size={15} /> Dashboard
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
